// Artwork pre-flight (section 3B of the NMS developer guide). Reads the PNG
// header chunks directly, so no image library is needed. The transparency
// pixel scan is injected because it needs a browser canvas.

export const MESSAGES = {
  type: "Upload Rejected: Invalid file type. All artwork must be submitted as a PNG file.",
  transparency: "Upload Rejected: No transparency detected. Your PNG must have a transparent background, otherwise a white box will be printed around your design.",
  dpi: "Upload Rejected: Resolution too low. Your file must be at least 300 DPI for high-quality printing.",
  dpiMissing: "Upload Rejected: This PNG has no resolution (DPI) information. Re-export it at 300 DPI or higher.",
  large: (tol) => `Upload Rejected: Artwork exceeds maximum size. Standard Front/Back decals must fit within a ${10 + tol}" x ${12 + tol}" area. Please resize your file or select the 'Oversized Decal' option.`,
  small: (tol) => `Upload Rejected: Artwork exceeds maximum size. Chest and sleeve decals must fit within a ${5 + tol}" x ${5 + tol}" area.`,
  custom: "Upload Rejected: Artwork dimensions do not match your requested custom size. Please ensure your file matches the exact inches you entered above.",
};

const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10];

/** Parse PNG chunks we care about. Returns null if not a PNG. */
export function readPngInfo(buffer) {
  const bytes = new Uint8Array(buffer);
  if (bytes.length < 33 || PNG_SIGNATURE.some((b, i) => bytes[i] !== b)) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const info = { width: 0, height: 0, colorType: -1, hasTrns: false, dpiX: null, dpiY: null };
  let off = 8;
  while (off + 8 <= bytes.length) {
    const len = view.getUint32(off);
    const type = String.fromCharCode(...bytes.subarray(off + 4, off + 8));
    const data = off + 8;
    if (type === "IHDR") {
      info.width = view.getUint32(data);
      info.height = view.getUint32(data + 4);
      info.colorType = bytes[data + 9];
    } else if (type === "tRNS") {
      info.hasTrns = true;
    } else if (type === "pHYs" && bytes[data + 8] === 1) {
      // unit 1 = pixels per metre
      info.dpiX = view.getUint32(data) * 0.0254;
      info.dpiY = view.getUint32(data + 4) * 0.0254;
    } else if (type === "IDAT" || type === "IEND") {
      break; // all metadata we need precedes image data
    }
    off = data + len + 4;
  }
  // Colour types 4 and 6 carry an alpha channel; 0/2/3 only via tRNS.
  info.canBeTransparent = info.colorType === 4 || info.colorType === 6 || info.hasTrns;
  return info;
}

const fits = (w, h, maxA, maxB) =>
  (w <= maxA && h <= maxB) || (w <= maxB && h <= maxA);

/**
 * Validate one artwork file for a decal.
 * @param {{name:string,type:string,arrayBuffer:()=>Promise<ArrayBuffer>}} file
 * @param {{placement:object, oversized?:boolean, widthIn?:number, heightIn?:number}} decal
 * @param {object} rules  config.artwork
 * @param {(file)=>Promise<boolean>} [hasTransparentPixels] browser pixel scan
 * @returns {Promise<{ok:boolean, error?:string, info?:object}>}
 */
export async function validateArtwork(file, decal, rules, hasTransparentPixels) {
  if (file.type && file.type !== rules.mime) return { ok: false, error: MESSAGES.type };
  const info = readPngInfo(await file.arrayBuffer());
  if (!info) return { ok: false, error: MESSAGES.type };

  if (!info.canBeTransparent) return { ok: false, error: MESSAGES.transparency, info };
  if (hasTransparentPixels && !(await hasTransparentPixels(file))) {
    return { ok: false, error: MESSAGES.transparency, info };
  }

  if (info.dpiX == null) return { ok: false, error: MESSAGES.dpiMissing, info };
  const dpi = Math.min(info.dpiX, info.dpiY);
  // pHYs stores whole pixels per metre, so 300 DPI saves as 299.9994.
  if (dpi + 0.5 < rules.minDpi) return { ok: false, error: MESSAGES.dpi, info };

  const w = info.width / info.dpiX;
  const h = info.height / info.dpiY;
  const tol = rules.sizeToleranceIn;
  const result = { ...info, widthIn: w, heightIn: h };
  if (decal.oversized) {
    if (!fits(w, h, Number(decal.widthIn) + tol, Number(decal.heightIn) + tol)) {
      return { ok: false, error: MESSAGES.custom, info: result };
    }
  } else {
    const [a, b] = decal.placement.maxIn;
    if (!fits(w, h, a + tol, b + tol)) {
      return { ok: false, error: decal.placement.kind === "large" ? MESSAGES.large(tol) : MESSAGES.small(tol), info: result };
    }
  }
  return { ok: true, info: result };
}

/** Browser-only: true if any pixel has alpha < 255. */
export async function browserHasTransparentPixels(file) {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, 1024 / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * scale));
  const h = Math.max(1, Math.round(bmp.height * scale));
  const canvas = typeof OffscreenCanvas !== "undefined" ? new OffscreenCanvas(w, h) : Object.assign(document.createElement("canvas"), { width: w, height: h });
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bmp, 0, 0, w, h);
  const px = ctx.getImageData(0, 0, w, h).data;
  for (let i = 3; i < px.length; i += 4) if (px[i] < 255) return true;
  return false;
}
